import { Utils } from "ng/src/app/core/utils";
import { LogService } from "ng/src/app/logs/log.service";
import { DestroyableComponent } from "ng/src/app/shared/destroyable.component";
import { EditorTreeNode } from 'ng/src/app/templates/editor/template-editor.component';
import { ReadonlyContainerEntry } from 'ng/src/app/templates/shared/template-container/template-container.model';
import { TemplateSegment, Template, TemplateSegmentTypeId, TemplateTypeId } from 'ng/src/app/templates/shared/template.model';
import { Injectable } from "@angular/core";
import { TemplateService } from "ng/src/app/templates/shared/template.service";
import { Observable, of, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { BasicRecord, Optional } from '../../shared/model/app.model';

@Injectable({ providedIn: 'root' })
export class TemplateSegmentClipboardService extends DestroyableComponent {
	private _value: Array<ClipboardTreeNode> = [];
	private _contextTemplate?: Template;
	
	private templateMap: Record<number, Template> = {};
	
	private readonly onDeleteSubject = new Subject<TemplateSegment[]>();
	readonly onDelete$ = this.onDeleteSubject.asObservable();
	
	private readonly onChangeSubject = new Subject<Array<ClipboardTreeNode>>();
	readonly onChange$ = this.onChangeSubject.asObservable();
	
	private readonly onContextChangeSubject = new Subject<Optional<Template>>();
	readonly onContextChange$ = this.onContextChangeSubject.asObservable();
	
	constructor(
		private templateService: TemplateService
	) {
		super();
		LogService.selectedLog$.pipe(
			takeUntil(this.onDestroy$)
		)
			.subscribe(() => this.value = []);
	}
	
	get hasMultiTemplate() {
		return Object.keys(this.templateMap).length > 1;
	}
	
	get contextLogId() {
		return Optional.of(this._value[0])?.data?.logId;
	}
	
	get selectedTemplateContext() {
		return Optional.of(this._value[0])?.data?.templateId;
	}
	
	get value() {
		return this._value;
	}
	
	set value(val) {
		this._value = Array.from(val)
			.filter(n => !!n.data)
			.sort(Utils.createMultiSortFunction([
				{ key: n => n.data.templateId },
				{ key: n => n.data.sortOrder }
			])
		);
		this.updateTemplateMap();
		this.onChangeSubject.next(val);
	}
	
	get contextTemplate() {
		return this._contextTemplate;
	}
	
	set contextTemplate(val) {
		this._contextTemplate = val;
		if (val) {
			this.templateMap[val.id] = val;
		}
		this.updateTemplateMap();
		this.onContextChangeSubject.next(val);
	}
	
	get contextTemplateType() {
		return this._contextTemplate?.templateType?.id;
	}
	
	get inSelectedTemplateContext() {
		return this._contextTemplate?.id === this.selectedTemplateContext
	}
	
	getTemplate(id?: number) {
		return Utils.isValidNumber(id) ? this.templateMap[id] : undefined;
	}
	
	remove(...nodeOrId: Array<ClipboardTreeNode | number>) {
		for (const node of nodeOrId) {
			const idx = this.value.findIndex(n => {
				if (typeof node === 'number') {
					return node === n.data.id;
				}
				return n === node;
			});
			
			if (idx < 0) {
				continue;
			}
			
			this.value.splice(idx, 1);
			this.value = this.value;
		}
	}
	
	clear() {
		this.value = [];
	}
	
	deleteSelected() {
		const segmentMap: BasicRecord<TemplateSegment> = {};
		
		this.value.forEach(n => {
			const segment = n.data;
			const template = this.templateMap[segment.templateId];
			if (!template) {
				throw new Error(`Template not found for segment ${segment.id}, templateId: ${segment.templateId}`);
			}
			
			segmentMap[segment.id] = segment;
			
			//If the segment is a sub-template, we need delete its children as well
			if (segment.type.id === TemplateSegmentTypeId.SUB_TEMPLATE && template.templateType.id === TemplateTypeId.OPERATIONAL) {
				n.children.forEach((childNode) => {
					if (childNode.data.obsolete) {
						return;
					}
					
					segmentMap[childNode.data.id] = childNode.data;
				})
			}
		});
		
		if (Object.keys(segmentMap).length === 0) {
			return;
		}
		
		const segments = Object.values(segmentMap);
		
		const ob$: Observable<any> = ((segments.length == 0)
			? of([])
			: this.templateService.deleteTemplateSegment(...segments));
		
		ob$.subscribe(() => {
			this.value = [];
			this.onDeleteSubject.next(segments);
		})
	}
	
	has(id: number | ClipboardTreeNode) {
		const idNum = Utils.isValidNumber(id) ? id : id.data.id;
		return this.value.some(e => e.data.id === idNum);
	}
	
	private updateTemplateMap() {
		const map = Utils.collectToMap(this.value, node => node.data.templateId);
		Object.keys(this.templateMap).forEach(templateKey => {
			if (!(templateKey in map) && templateKey != this.contextTemplate?.id?.toString()) {
				delete this.templateMap[templateKey as any];
			}
		});
	}
}

export type ClipboardTreeNode = {
		children: ClipboardTreeNode[];
		containerEntry: ReadonlyContainerEntry;
		data: TemplateSegment;
	}
	& EditorTreeNode;